"use client";

import { useState } from "react";
import { LTM_API_BASE } from "@/lib/places";
import { ChipGroup, fieldLabelClass, inputClass } from "./FormChips";

const TRANSPORT_OPTIONS = ["Car", "Bike", "Bus", "Train", "Flight", "Walk"].map((v) => ({ value: v, label: v }));
const TRIP_TYPE_OPTIONS = ["Cultural", "Adventure", "Food", "Nature", "Business", "Family", "Spiritual", "Relaxation"].map((v) => ({
  value: v,
  label: v,
}));
const FOOD_OPTIONS = ["Any", "Vegetarian", "Non-Veg", "Vegan", "Jain"].map((v) => ({ value: v, label: v }));
const GROUP_TYPE_OPTIONS = ["Solo", "Couple", "Friends", "Family"].map((v) => ({ value: v, label: v }));
const BUDGET_OPTIONS = ["Budget", "Medium", "Luxury"].map((v) => ({ value: v, label: v }));

export default function GuideRequestForm() {
  const [from, setFrom] = useState("");
  const [destination, setDestination] = useState("");
  const [days, setDays] = useState(3);
  const [transport, setTransport] = useState("Car");
  const [tripType, setTripType] = useState("Cultural");
  const [food, setFood] = useState("Any");
  const [groupType, setGroupType] = useState("Solo");
  const [groupSize, setGroupSize] = useState(1);
  const [budget, setBudget] = useState("Medium");
  const [notes, setNotes] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!from.trim() || !destination.trim() || !contactName.trim() || (!contactEmail.trim() && !contactPhone.trim())) {
      setStatus("error");
      setErrorMessage("Please fill in your name, destination, and at least one contact method.");
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch(`${LTM_API_BASE}/guide-requests/public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from: from.trim(),
          destination: destination.trim(),
          days,
          transport,
          tripType,
          food,
          groupType,
          groupSize,
          budget,
          notes: notes.trim(),
          contactName: contactName.trim(),
          contactEmail: contactEmail.trim() || undefined,
          contactPhone: contactPhone.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Something went wrong. Please try again.");
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/60 p-8 text-center dark:border-emerald-800/60 dark:bg-emerald-500/5">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-2xl dark:bg-emerald-500/20">✓</div>
        <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">Request Sent!</h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          A local guide will review your request and get back to you at the contact details you provided, usually within
          24-48 hours.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl border border-slate-200/80 bg-white/80 p-6 backdrop-blur-sm dark:border-slate-800 dark:bg-[#0f172a]/80">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Traveling From *</label>
          <input type="text" required value={from} onChange={(e) => setFrom(e.target.value)} placeholder="e.g. Chennai" className={inputClass} />
        </div>
        <div>
          <label className={fieldLabelClass}>Destination *</label>
          <input
            type="text"
            required
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="e.g. Kanyakumari"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Number of Days</label>
          <input
            type="number"
            min={1}
            max={30}
            value={days}
            onChange={(e) => setDays(Math.min(30, Math.max(1, Number(e.target.value) || 1)))}
            className={inputClass}
          />
        </div>
        <div>
          <label className={fieldLabelClass}>Group Size</label>
          <input
            type="number"
            min={1}
            max={20}
            value={groupSize}
            onChange={(e) => setGroupSize(Math.min(20, Math.max(1, Number(e.target.value) || 1)))}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={fieldLabelClass}>How Will You Travel?</label>
        <div className="mt-1.5"><ChipGroup options={TRANSPORT_OPTIONS} value={transport} onChange={setTransport} /></div>
      </div>
      <div>
        <label className={fieldLabelClass}>Trip Type</label>
        <div className="mt-1.5"><ChipGroup options={TRIP_TYPE_OPTIONS} value={tripType} onChange={setTripType} /></div>
      </div>
      <div>
        <label className={fieldLabelClass}>Who's Traveling?</label>
        <div className="mt-1.5"><ChipGroup options={GROUP_TYPE_OPTIONS} value={groupType} onChange={setGroupType} /></div>
      </div>
      <div>
        <label className={fieldLabelClass}>Food Preference</label>
        <div className="mt-1.5"><ChipGroup options={FOOD_OPTIONS} value={food} onChange={setFood} /></div>
      </div>
      <div>
        <label className={fieldLabelClass}>Budget</label>
        <div className="mt-1.5"><ChipGroup options={BUDGET_OPTIONS} value={budget} onChange={setBudget} /></div>
      </div>

      <div>
        <label className={fieldLabelClass}>Anything Else Your Guide Should Know?</label>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Interests, accessibility needs, must-see places..."
          className={inputClass}
        />
      </div>

      <div className="border-t border-slate-200 pt-6 dark:border-slate-800">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">Your Contact Details</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">So a local guide can reach you with a personalized plan.</p>
        <div className="mt-4 grid gap-5 sm:grid-cols-3">
          <div>
            <label className={fieldLabelClass}>Your Name *</label>
            <input type="text" required value={contactName} onChange={(e) => setContactName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={fieldLabelClass}>Email</label>
            <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={fieldLabelClass}>Phone</label>
            <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className={inputClass} />
          </div>
        </div>
      </div>

      {status === "error" ? (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">{errorMessage}</p>
      ) : null}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "submitting" ? "Sending Request..." : "Request a Local Guide"}
      </button>
    </form>
  );
}
